import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Logo from "../assets/LogoNew2.png";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dashboard,
  People,
  Person,
  AttachMoney,
  Logout,
  Settings,
  Business,
  Assessment,
  HowToReg,
  AdminPanelSettings,
  SupervisorAccount,
  History,
  Security,
  ReceiptLong,
  Gavel,
  RateReview,
  FilterAlt,
} from "@mui/icons-material";
import { useState, useEffect } from "react";
import { Avatar, Menu, MenuItem, Tooltip, Chip } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { toggleSidebar, setSidebarOpen } from "../redux/reducer/SidebarSlice";
import AdminLogoutDialog from "./AdminLogoutDialog";
import {
  ROLE_LABELS,
  ROLE_COLORS,
  getVisibleNavItems,
  normalizeRole,
} from "../config/adminRoles";

const drawerWidth = 260;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  background: "linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  background: "linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)",
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  background: "linear-gradient(90deg, #1a1a2e 0%, #16213e 100%)",
  ...(open && {
    [theme.breakpoints.up("md")]: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
    },
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginLeft: 0,
  },
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

// Icon mapping for menu items
const iconMap = {
  Dashboard: <Dashboard />,
  "All Users": <People />,
  Landlords: <HowToReg />,
  Tenants: <Person />,
  Properties: <Business />,
  Revenue: <AttachMoney />,
  Reports: <Assessment />,
  "Admin Management": <SupervisorAccount />,
  "Activity Logs": <History />,
  Settings: <Settings />,
  "Commission Settings": <AttachMoney />,
  "OTS Fee Receipts": <ReceiptLong />,
  "Follow-ups": <FilterAlt />,
  "Move-outs & Dues": <Business />,
  "Owner Payouts": <AttachMoney />,
  "Violation Escalations": <Gavel />,
  "Property Reviews": <RateReview />,
};

export default function AdminWrapper() {
  const location = useLocation();
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const user = validateUser?.UserValidate?.data?.user;

  // No role means no access: the menu below stays empty rather than
  // falling back to some default role's pages.
  const adminRole = normalizeRole(user?.adminRole);

  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const handleDrawerClose = () => {
    dispatch(toggleSidebar());
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile && open) {
        dispatch(setSidebarOpen(false));
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, dispatch]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Only the pages this admin's permissions open
  const visibleMenuItems = getVisibleNavItems(user);
  const canOpen = (path) => visibleMenuItems.some((item) => item.path === path);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => dispatch(toggleSidebar())}
              edge="start"
            >
              <MenuIcon />
            </IconButton>

            <Box
              component="img"
              sx={{ height: 50,width:100, margin: 1 }}
              alt="OTS Logo"
              src={Logo}
            />

            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", ml: 2 }}>
              <Security sx={{ mr: 1, color: "#e94560" }} />
              <Typography variant="h6" noWrap component="div">
                Admin Portal
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Role Badge */}
              <Chip
                label={ROLE_LABELS[adminRole] || "Admin"}
                size="small"
                sx={{
                  bgcolor: `${ROLE_COLORS[adminRole] || "#6366f1"}20`,
                  color: ROLE_COLORS[adminRole] || "#6366f1",
                  fontWeight: 600,
                  display: { xs: "none", sm: "flex" },
                }}
              />

              <Typography variant="body2" sx={{ display: { xs: "none", md: "block" } }}>
                {user?.name || "Admin"}
              </Typography>

              <Tooltip title="Account settings">
                <IconButton onClick={handleClick} size="small">
                  <Avatar
                    src={user?.profileImage}
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: ROLE_COLORS[adminRole] || "#e94560",
                    }}
                  >
                    {(user?.name || "A").charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleClose}
              onClick={handleClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                elevation: 3,
                sx: { mt: 1.5, minWidth: 200 },
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e5e7eb" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.name || "Administrator"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={ROLE_LABELS[adminRole]}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: 11,
                      bgcolor: `${ROLE_COLORS[adminRole]}15`,
                      color: ROLE_COLORS[adminRole],
                    }}
                  />
                </Box>
              </Box>
              {canOpen("/admin-dashboard") && (
                <MenuItem onClick={() => navigate("/admin-dashboard")}>
                  <ListItemIcon>
                    <Dashboard fontSize="small" />
                  </ListItemIcon>
                  Dashboard
                </MenuItem>
              )}
              {canOpen("/admin/settings") && (
                <MenuItem onClick={() => navigate("/admin/settings")}>
                  <ListItemIcon>
                    <Settings fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={() => setOpenDialog(true)} sx={{ color: "#ef4444" }}>
                <ListItemIcon>
                  <Logout fontSize="small" sx={{ color: "#ef4444" }} />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={open}
          onClose={() => dispatch(setSidebarOpen(false))}
        >
          <DrawerHeader sx={{ justifyContent: "space-between", px: 2 }}>
            {open && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AdminPanelSettings sx={{ color: "#e94560" }} />
                <Typography variant="subtitle1" sx={{ color: "#fff", fontWeight: 600 }}>
                  Admin Panel
                </Typography>
              </Box>
            )}
            <IconButton onClick={handleDrawerClose} sx={{ color: "#fff" }}>
              {theme.direction === "rtl" ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </DrawerHeader>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

          {/* Role indicator in sidebar */}
          {open && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Chip
                icon={<Security sx={{ fontSize: 14, color: "inherit" }} />}
                label={ROLE_LABELS[adminRole]}
                size="small"
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  bgcolor: `${ROLE_COLORS[adminRole]}20`,
                  color: ROLE_COLORS[adminRole],
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: "inherit" },
                }}
              />
            </Box>
          )}

          <List sx={{ px: 1, pt: 1 }}>
            {visibleMenuItems.map((item, index) => (
              <ListItem
                key={index}
                disablePadding
                sx={{ display: "block", mb: 0.5 }}
                onClick={() => navigate(item.path)}
              >
                <Tooltip title={!open ? item.title : ""} placement="right">
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2,
                      borderRadius: 2,
                      backgroundColor: isActive(item.path)
                        ? "rgba(233, 69, 96, 0.2)"
                        : "transparent",
                      borderLeft: isActive(item.path)
                        ? "3px solid #e94560"
                        : "3px solid transparent",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 2 : "auto",
                        justifyContent: "center",
                        color: isActive(item.path) ? "#e94560" : "#9ca3af",
                      }}
                    >
                      {iconMap[item.title] || <Dashboard />}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      sx={{
                        opacity: open ? 1 : 0,
                        "& .MuiTypography-root": {
                          color: isActive(item.path) ? "#fff" : "#9ca3af",
                          fontWeight: isActive(item.path) ? 600 : 400,
                          fontSize: "0.95rem",
                        },
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>

          <Box sx={{ flexGrow: 1 }} />

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <List sx={{ px: 1, pb: 2 }}>
            <ListItem disablePadding sx={{ display: "block" }}>
              <Tooltip title={!open ? "Logout" : ""} placement="right">
                <ListItemButton
                  onClick={() => setOpenDialog(true)}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? "initial" : "center",
                    px: 2,
                    borderRadius: 2,
                    "&:hover": {
                      backgroundColor: "rgba(233, 69, 96, 0.15)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 2 : "auto",
                      justifyContent: "center",
                      color: "#ef4444",
                    }}
                  >
                    <Logout />
                  </ListItemIcon>
                  <ListItemText
                    primary="Logout"
                    sx={{
                      opacity: open ? 1 : 0,
                      "& .MuiTypography-root": {
                        color: "#ef4444",
                        fontWeight: 500,
                      },
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>
        </Drawer>

        <Box component="main" sx={{ flexGrow: 1, p: 3, marginTop: 8 }} />
      </Box>
      <AdminLogoutDialog open={openDialog} onClose={() => setOpenDialog(false)} />
    </>
  );
}
